import { useCallback, useEffect, useRef, useState } from "react";
import * as ml from "../../api/ml";
import { getProject } from "../../api/projects";
import type { Project } from "../../api/types";
import { BAND_LABEL, band } from "../../lib/results";
import { modelFacts, trainedOn } from "../../lib/models";
import { navigate } from "../../router";
import { projectDataChanged } from "../../state/currentProject";
import { useSession } from "../../state/session";
import { useDialogs } from "../../ui/dialogs";
import { Icon } from "../../ui/icons";
import { useModelActions } from "../../ui/useModelActions";
import { ModelTestPanel } from "./ModelTestPanel";

/**
 * Models: every version this project has trained or imported, newest first, with what it is, how good it is, and what
 * to do with it (download, export for deployment, auto-annotate with it). Import weights lives here too.
 * Web only for now (docs/DESKTOP_PORT_BACKLOG.md).
 */
export function ModelsScreen({ projectId }: { projectId: number }) {
  const { canEdit } = useSession();
  const dlg = useDialogs();
  const [project, setProject] = useState<Project | null>(null);
  const [stats, setStats] = useState<ml.AnnotationStats | null>(null);
  const file = useRef<HTMLInputElement>(null);
  // The version being tried on test pictures (Test…), or null with the panel closed.
  const [testing, setTesting] = useState<number | null>(null);
  const testPanel = useRef<HTMLDivElement>(null);
  const openTest = (version: number) => {
    setTesting(version);
    requestAnimationFrame(() => testPanel.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  const refresh = useCallback(async () => {
    try { setStats(await ml.annotationStats(projectId)); } catch { setStats(null); }
    projectDataChanged();
  }, [projectId]);
  useEffect(() => {
    getProject(projectId).then(setProject).catch(() => {});
    void refresh();
  }, [projectId, refresh]);

  const model = useModelActions({ id: projectId, name: project?.name ?? "model", deployTarget: project?.deploy_target },
    stats?.latest_model_version ?? null, refresh);
  const versions = [...model.versions].sort((a, b) => b.version_number - a.version_number);
  const busy = model.saving || model.converting;
  const anyRemoved = versions.some((v) => !v.available);
  const toggleKeep = async (v: ml.ModelVersionInfo) => {
    try { model.replaceVersion(await ml.keepModel(projectId, v.version_number, !v.keep)); }
    catch (e) { await dlg.alert({ title: "Could not change Keep", text: (e as Error).message }); }
  };

  return (
    <div className="train-page" data-testid="models-page">
      <div className="page-head">
        <div>
          <h1 className="page-title" data-testid="models-title">Models — {project?.name ?? ""}</h1>
          <div className="muted">Every version trained or imported in this project. Exports and auto-annotation use the latest unless you choose another here.</div>
        </div>
        <span className="spacer" />
        <button className="btn" disabled={!canEdit || model.importing !== null} onClick={() => file.current?.click()} data-testid="models-import"
          title={canEdit ? "Load a best.pt trained elsewhere. It becomes this project's latest version." : "Your role can look at projects but not change them"}>
          {model.importing ? "Checking…" : <><Icon name="import" />Import weights…</>}</button>
        <input ref={file} type="file" accept=".pt" hidden data-testid="input-model"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) void model.runImport(f, false); }} />
      </div>

      {versions.length === 0 ? (
        <section className="card-section empty-models" data-testid="models-empty">
          <Icon name="cube" size={28} />
          <h2>No model yet</h2>
          <div className="muted">Train one from this project's reviewed annotations, or import weights trained on another machine.</div>
          <div className="row" style={{ justifyContent: "center" }}>
            <button className="btn primary" disabled={!canEdit} onClick={() => navigate({ name: "train", projectId })} data-testid="models-go-train"><Icon name="target" />Go to Train</button>
          </div>
        </section>
      ) : (
        <section className="card-section" style={{ padding: 0, overflowX: "auto" }}>
          <table className="models-table" data-testid="models-table">
            <thead>
              <tr><th>Version</th><th>Score</th><th>Model</th><th>Trained on</th><th>Created</th><th aria-label="Actions" /></tr>
            </thead>
            <tbody>
              {versions.map((v) => {
                const score = typeof v.best_map50 === "number" ? v.best_map50 : null;
                const b = score === null ? null : band(score);
                return (
                  <tr key={v.version_number} data-testid={`model-row-${v.version_number}`} className={v.available ? undefined : "missing"}>
                    <td>
                      <span className="model-version">v{v.version_number}</span>
                      {v.is_latest && <span className="pill none" style={{ marginLeft: 8 }}>Latest</span>}
                      {v.best && <span className="pill none" style={{ marginLeft: 8 }} data-testid={`model-best-${v.version_number}`}
                        title="The best validation score of all versions. Its weights are always kept.">Best</span>}
                    </td>
                    <td data-testid={`model-score-${v.version_number}`}>
                      {b ? <span className={`score ${b}`} title="mAP@0.5 on the held-back validation images: 0.8 and above is good, under 0.5 needs work.">
                        {score!.toFixed(2)} · {BAND_LABEL[b]}</span>
                        : <span className="muted">{v.source === "imported" ? "Not scored here" : "—"}</span>}
                    </td>
                    <td>{modelFacts(v)}</td>
                    <td className="muted">{trainedOn(v)}</td>
                    <td className="muted">{/^\d{4}-\d{2}-\d{2}/.exec(v.created_at)?.[0] ?? ""}</td>
                    <td className="model-actions">
                      {!v.available ? <span className="muted" data-testid={`model-removed-${v.version_number}`}
                        title="This version's weights are no longer stored, so it cannot be downloaded, exported or tested. Its score and history stay.">Weights removed</span> : <>
                        <button className={`btn flat${v.keep ? " on" : ""}`} aria-pressed={!!v.keep} disabled={!canEdit} onClick={() => void toggleKeep(v)}
                          data-testid={`model-keep-${v.version_number}`}
                          title={v.keep ? "Kept for good. Click to let its weights be removed with other old versions'."
                            : "Never remove this version's weights. Old versions' weights are otherwise removed to save space."}>
                          <Icon name="shield" />{v.keep ? "Kept" : "Keep"}</button>
                        <button className="btn flat" disabled={busy} onClick={() => void model.saveVersion(v.version_number)} data-testid={`model-download-${v.version_number}`}
                          title="Download the weights (.pt)"><Icon name="download" />Weights</button>
                        <button className="btn flat" disabled={busy} onClick={() => model.exportVersion(v.version_number)} data-testid={`model-export-${v.version_number}`}
                          title="Package this version for the device it will run on"><Icon name="cube" />Export…</button>
                        <button className="btn flat" disabled={!canEdit} onClick={() => openTest(v.version_number)} data-testid={`model-test-${v.version_number}`}
                          title={canEdit ? "See what this version finds on test pictures from your computer, drawn over them. Nothing is saved." : "Your role can look at projects but not run models"}>
                          <Icon name="eye" />Test…</button>
                        <button className="btn" disabled={!canEdit} onClick={() => navigate({ name: "auto", projectId, version: v.version_number })} data-testid={`model-auto-${v.version_number}`}
                          title="Let this version annotate the images not annotated yet"><Icon name="wand" />Auto-annotate</button>
                      </>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}
      {anyRemoved && (
        <div className="muted" data-testid="models-removed-note">
          Older versions' weights are removed to save space. The newest versions, the best-scoring one and any marked Keep keep
          theirs; a version whose weights were removed keeps its score and history.
        </div>
      )}
      {model.converting && <div className="muted" data-testid="models-status">Exporting…</div>}
      {testing !== null && (
        <div ref={testPanel}>
          <ModelTestPanel projectId={projectId} version={testing} onClose={() => setTesting(null)} />
        </div>
      )}
      {model.panel}
    </div>
  );
}
